import * as React from 'react';
import gql from 'graphql-tag';
import AuthorizedQuery from './AuthorizedQuery';
import { QueryResult } from 'react-apollo';

const FARM_QUERY = gql(`
  query Library($farmId: ID!, $filter: FarmDocumentFilter, $first: Int, $after: String) {
    farm(id: $farmId) {
      id
      name
      library(filter: $filter, first: $first, after: $after) {
        nodes {
          id
          kind
          thumbnailUrl
          createdOn
          capturedOn
          ... on RasterDocument {
            fieldId
            sceneSource
            meanNdvi
            rasterUrl
          }
          ... on ReportDocument {
            type
            downloadUrl
          }
          ... on YieldAnalysisDocument {
            rasterUrl
          }
          ... on ZoneAnalysisDocument {
            rasterUrl
          }
        }
        pageInfo {
          nextToken
          hasNext
        }
      }
    }
  }
`);

const handleFetchMore = (
  fetchMore: any,
  props: Props,
  documents: LibraryDocument[],
  nextToken?: string
) => {
  if (nextToken) {
    fetchMore({
      variables: {
        farmId: props.farmId,
        filter: props.filter,
        first: props.first,
        after: nextToken
      },
      updateQuery: (prev: any, { fetchMoreResult }: any) => {
        return {
          ...prev,
          farm: {
            ...prev.farm,
            library: {
              ...prev.farm.library,
              nodes: [
                ...prev.farm.library.nodes,
                ...fetchMoreResult.farm.library.nodes
              ],
              pageInfo: fetchMoreResult.farm.library.pageInfo
            }
          }
        };
      }
    });
  }
};

interface DocumentFilter {
  kind?: string[];
  fieldId?: string | null;
  criticalMonths?: number[] | null;
  capturedUntil?: number | null;
  capturedSince?: number | null;
  minMeanNdvi?: number | null;
  maxMeanNdvi?: number | null;
}

interface Result {
  farm: Farm | null;
  documents: AnyDocument[] | null;
  loading: boolean;
  fetchMore: () => void;
  refetch: () => void;
}

type ChildrenFn = (result: Result) => React.ReactNode;

interface Props {
  farmId?: string;
  filter: DocumentFilter;
  first: number;
  children: ChildrenFn;
}

const mapPropsToVariables = (props: Props) => ({
  farmId: props.farmId,
  filter: props.filter,
  first: props.first
});

const renderChildren = (props: Props, result: QueryResult): React.ReactNode => {
  const farm = (result.data && result.data.farm) || null;
  const library = farm && farm.library;
  const documents = (library && library.nodes) || null;
  const nextToken = library && library.pageInfo && library.pageInfo.nextToken;

  const fetchMoreWrapper = () =>
    handleFetchMore(result.fetchMore, props, documents, nextToken);
  const refetchWrapper = () => result.refetch(mapPropsToVariables(props));

  return props.children({
    farm,
    documents,
    loading: result.loading,
    fetchMore: fetchMoreWrapper,
    refetch: refetchWrapper
  });
};

const FarmLibraryProvider: React.StatelessComponent<Props> = (props: Props) => (
  <AuthorizedQuery
    query={FARM_QUERY}
    skip={!props.farmId}
    variables={mapPropsToVariables(props)}
    notifyOnNetworkStatusChange={true}
    children={result => renderChildren(props, result)}
  />
);

export default FarmLibraryProvider;



// WEBPACK FOOTER //
// ./src/providers/FarmLibraryProvider.tsx